import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { getAllVideoDetails, getVideoDetail, mockComments } from '@/mocks/contentData';
import CommentSection from './components/CommentSection';

export default function VideoDetail() {
  const { id = 'v001' } = useParams<{ id: string }>();
  const video = getVideoDetail(id);

  if (!video) {
    return (
      <main className="pt-16">
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100">
            <i className="ri-error-warning-line text-foreground-600 text-2xl"></i>
          </div>
          <p className="text-foreground-500">视频不存在或已被删除</p>
          <Link
            to="/"
            className="px-4 py-2 bg-background-100 hover:bg-background-200 text-foreground-300 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedVideos = getAllVideoDetails().filter((v) => v.id !== id).slice(0, 3);

  return (
    <main className="pt-16">
      {/* Video player area */}
      <section className="relative bg-background-50">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
            {/* Main content */}
            <div>
              {/* Video player placeholder */}
              <div className="relative aspect-video rounded-2xl overflow-hidden bg-background-100 border border-background-200/70 mb-6">
                <img
                  src={video.coverImage}
                  alt={video.title}
                  title={`黑料视频 - ${video.title}`}
                  className="w-full h-full object-cover"
                />
                {/* Play overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-background-950/40 hover:bg-background-950/30 transition-colors cursor-pointer group">
                  <div className="w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-play-fill text-background-950 text-2xl ml-0.5"></i>
                    </span>
                  </div>
                </div>
                {/* Duration */}
                <span className="absolute bottom-4 right-4 px-3 py-1 bg-background-950/80 text-foreground-200 text-sm font-medium rounded-lg">
                  {video.duration}
                </span>
              </div>

              {/* Video info */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Link
                    to={`/category/${getCategorySlug(video.category)}`}
                    className="px-2.5 py-1 bg-primary-500/15 text-primary-400 text-xs font-semibold rounded-full hover:bg-primary-500/25 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {video.category}
                  </Link>
                  <span className="text-xs text-foreground-700">{video.publishTime}</span>
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground-50 mb-4 leading-snug">
                  {video.title}
                </h1>

                {/* Stats */}
                <div className="flex items-center gap-6 text-sm text-foreground-500 mb-6 pb-6 border-b border-background-200/60">
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </span>
                    {video.views} 次观看
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-chat-3-line"></i>
                    </span>
                    {video.comments.toLocaleString()} 条评论
                  </span>
                  <button className="flex items-center gap-1.5 text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer ml-auto">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-share-forward-line"></i>
                    </span>
                    分享
                  </button>
                </div>

                {/* Content */}
                <div className="text-sm text-foreground-400 leading-relaxed whitespace-pre-line">
                  {video.content}
                </div>

                {/* Tags */}
                <div className="flex items-center gap-2 mt-6 flex-wrap">
                  {video.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-background-100 border border-background-200/70 text-foreground-500 text-xs rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Comments */}
              <CommentSection videoId={video.id} />
            </div>

            {/* Sidebar */}
            <div className="hidden lg:block">
              <div className="sticky top-20">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-500/15">
                    <i className="ri-fire-line text-accent-400 text-base"></i>
                  </div>
                  <h3 className="text-base font-bold text-foreground-100">相关猛料</h3>
                </div>
                <div className="space-y-4">
                  {relatedVideos.map((rv) => (
                    <Link
                      key={rv.id}
                      to={`/video/${rv.id}`}
                      className="flex gap-3 group cursor-pointer"
                    >
                      <div className="w-[120px] h-[68px] rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={rv.coverImage}
                          alt={rv.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-semibold text-foreground-300 line-clamp-2 leading-snug group-hover:text-primary-400 transition-colors">
                          {rv.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-foreground-700">
                          <span>{rv.views} 次观看</span>
                          <span>·</span>
                          <span>{rv.duration}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Stats card */}
                <div className="mt-8 p-5 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-lg font-black text-primary-400">{mockComments.length}</div>
                      <div className="text-[10px] text-foreground-700 mt-1">条评论</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-black text-primary-400">{video.views}</div>
                      <div className="text-[10px] text-foreground-700 mt-1">次观看</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function getCategorySlug(category: string): string {
  const map: Record<string, string> = {
    '明星八卦': 'mingxing-bagua',
    '网红黑料': 'wanghong-heiliao',
    '暴走视频': 'baozou-shipin',
    '内幕大瓜': 'neimu-dagua',
  };
  return map[category] || 'mingxing-bagua';
}
